import React from 'react';
import { isEmpty } from 'lodash';

import { showApiErrorToast } from '@packages/shared/utils';
import { TemplateActionMenu } from '@packages/ui/composites';
import type { TemplateActionMenuItem } from '@packages/ui/composites';
import { Button } from '@packages/ui/primitives/Button';
import { Typography } from '@packages/ui/primitives/Typography';
import { ImportFileInput } from '@packages/ui/patterns/ImportFileInput';
import { DataTable } from '@packages/ui/patterns/DataTable';
import type { ButtonVariant } from '@packages/ui/primitives/Button/buttonTypes';
import {
  resolveImportExportFileInputId,
  resolveImportExportSemanticId,
} from '../utils/importExportSemanticIds';

const InputField = DataTable.FileInputLayout;

interface TableImportBlockProps {
  dropdownEnable: boolean;
  templateDatas: any;
}

interface TemplateOption {
  title?: string;
  semanticId?: string;
  import?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<unknown>;
  download?: () => void;
  permission?: React.ReactElement;
}

interface TemplateGroup {
  title?: string;
  semanticId?: string;
  groupType?: 'download' | 'upload';
  options?: TemplateOption[];
  download?: () => void;
  import?: (event: React.ChangeEvent<HTMLInputElement>) => Promise<unknown>;
  permission?: React.ReactElement;
}

const resolveTriggerSemanticId = (item: { semanticId?: string; title?: string }): string | undefined =>
  resolveImportExportSemanticId(item.semanticId, item.title ?? '');

const resolveMenuVariant = (item: TemplateGroup): { variant: ButtonVariant; icon: string } => {
  const isDownload = item.groupType === 'download';
  return {
    variant: isDownload ? 'primary-dark' : 'primary-outline',
    icon: isDownload ? 'cloud_download' : 'upload',
  };
};

const resolveSimpleVariant = (item: TemplateGroup): { variant: ButtonVariant; icon: string } => {
  const isUpload = item.groupType === 'upload';
  return {
    variant: isUpload ? 'primary-outline' : 'secondary-white',
    icon: isUpload ? 'upload' : 'cloud_download',
  };
};

const wrapWithPermission = (
  permission: React.ReactElement | undefined,
  node: React.ReactNode,
): React.ReactNode => {
  if (!permission) return node;
  return React.cloneElement(permission, { children: node });
};

const TableImportBlock: React.FC<TableImportBlockProps> = (props) => {
  const { templateDatas } = props;

  const handleImportValue = async (
    event: React.ChangeEvent<HTMLInputElement>,
    importFunction: (event: React.ChangeEvent<HTMLInputElement>) => Promise<unknown>,
  ): Promise<void> => {
    if (!isEmpty(event.target.files)) {
      try {
        await importFunction(event);
      } catch (error) {
        // OOP imports use ActionRegistry.executeAction → showApiErrorToast (deduped here).
        showApiErrorToast(error);
      }
    }
  };

  const renderFileInput = (
    option: TemplateOption,
    groupIndex: number,
    optionIndex: number,
  ): React.ReactNode => {
    const semanticId = resolveImportExportFileInputId(
      option.semanticId,
      option.title ?? '',
      groupIndex,
      optionIndex,
    );

    const fileInput = (
      <ImportFileInput
        semanticId={semanticId}
        accept=".xlsx, .xls"
        label={option.title ?? 'Import Value'}
        onChange={(event) => {
          if (option.import) {
            void handleImportValue(event, option.import);
          }
        }}
      />
    );

    return wrapWithPermission(option.permission, fileInput);
  };

  const renderTemplateData = (data: TemplateGroup[]) => {
    const dropdownItems: React.ReactNode[] = [];
    const simpleButtons: React.ReactNode[] = [];

    data.forEach((item, index) => {
      if (item.options) {
        const { variant, icon } = resolveMenuVariant(item);
        const menuItems: TemplateActionMenuItem[] = item.options.map((option) => {
          if (option.import) {
            const semanticId = resolveImportExportFileInputId(
              option.semanticId,
              option.title ?? '',
              index,
              item.options!.indexOf(option),
            );
            return {
              type: 'file' as const,
              label: option.title ?? 'Import Value',
              semanticId,
              accept: '.xlsx, .xls',
              permission: option.permission,
              onFile: (event: React.ChangeEvent<HTMLInputElement>) => {
                if (option.import) {
                  void handleImportValue(event, option.import);
                }
              },
            };
          }

          return {
            type: 'action',
            label: option.title ?? '',
            semanticId: resolveImportExportSemanticId(option.semanticId, option.title ?? ''),
            onSelect: () => option.download?.(),
          };
        });

        dropdownItems.push(
          <TemplateActionMenu
            key={index}
            triggerLabel={item.title ?? ''}
            triggerSemanticId={resolveTriggerSemanticId(item)}
            icon={icon}
            variant={variant}
            items={menuItems}
          />,
        );
        return;
      }

      const { variant, icon } = resolveSimpleVariant(item);

      if (item.download) {
        simpleButtons.push(
          <Button
            key={`download-${index}`}
            variant={variant}
            size="sm"
            icon={icon}
            label={item.title}
            semanticId={resolveTriggerSemanticId(item)}
            onClick={item.download}
          />,
        );
      }

      if (item.import) {
        simpleButtons.push(
          wrapWithPermission(
            item.permission,
            renderFileInput(item, index, 0),
          ) as React.ReactNode,
        );
      }
    });

    return (
      <>
        {dropdownItems.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {dropdownItems}
          </div>
        )}
        {simpleButtons.length > 0 && (
          <InputField>{simpleButtons}</InputField>
        )}
      </>
    );
  };

  return (
    <DataTable.ImportBar>
      <Typography variant="body-s">
        Quick &amp; easy to use CSV template to import large data in an instant.
      </Typography>
      {renderTemplateData(templateDatas)}
    </DataTable.ImportBar>
  );
};

export default TableImportBlock;
